import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { logout, setUserPreferences } from '@/auth/session';
import { useIsOwner, useProfile } from '@/auth/useSession';
import { toast } from '@/components/toast';
import { Button, Card, Dialog, Field, Input, PageHeader, Segmented } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { apiJson } from '@/lib/api';
import { useFormat } from '@/lib/format';
import { syncNow } from '@/sync/client';

/** Screen 27 — My profile (PL-3, PL-12): language, primary calendar, password (owner), logout guard. */
export function MePage() {
  const { t } = useTranslation();
  const f = useFormat();
  const profile = useProfile();
  const isOwner = useIsOwner();
  const navigate = useNavigate();
  const [guard, setGuard] = useState<number | null>(null);
  const [typed, setTyped] = useState('');
  const [pwOpen, setPwOpen] = useState(false);

  const setPref = async (patch: { locale?: 'am' | 'en'; primaryCalendar?: 'EC' | 'GC' }) => {
    await setUserPreferences(patch);
  };

  const tryLogout = async () => {
    const n = await db.outbox.count();
    if (n > 0) {
      setTyped('');
      setGuard(n);
      return;
    }
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader title={t('me.title')} />
      <Card className="space-y-1">
        <div className="text-lg font-semibold">{profile.name}</div>
        <div className="text-stone-600">
          {f.phone(profile.phone)} · {t(`users.role.${profile.role}`)}
        </div>
        <div className="text-stone-600">{profile.tenantName}</div>
      </Card>
      <Card className="space-y-4">
        <Field label={t('me.language')}>
          <Segmented value={profile.locale} onChange={(v) => void setPref({ locale: v })} options={[{ value: 'am', label: 'አማርኛ' }, { value: 'en', label: 'English' }]} />
        </Field>
        <Field label={t('me.calendar')}>
          <Segmented value={profile.primaryCalendar} onChange={(v) => void setPref({ primaryCalendar: v })} options={[{ value: 'EC', label: t('date.ecLong') }, { value: 'GC', label: t('date.gcLong') }]} />
        </Field>
      </Card>
      {isOwner ? (
        <Button variant="secondary" className="w-full" onClick={() => setPwOpen(true)}>
          {t('me.changePassword')}
        </Button>
      ) : null}
      <Button variant="danger" className="w-full" onClick={() => void tryLogout()} data-testid="logout">
        {t('me.logout')}
      </Button>

      <Dialog
        open={guard !== null}
        onOpenChange={(o) => !o && setGuard(null)}
        title={t('me.unsyncedTitle')}
        description={t('me.unsyncedBody', { count: guard ?? 0 })}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setGuard(null);
                void syncNow().then(() => toast(t('sync.syncing'), 'info'));
              }}
            >
              {t('sync.syncNow')}
            </Button>
            <Button
              variant="danger"
              disabled={typed !== String(guard)}
              onClick={async () => {
                setGuard(null);
                await logout();
                navigate('/login', { replace: true });
              }}
            >
              {t('me.discardN', { count: guard ?? 0 })}
            </Button>
          </>
        }
      >
        <Field label={t('me.typeToConfirm', { count: guard ?? 0 })}>
          <Input inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="discard-confirm" />
        </Field>
      </Dialog>
      {pwOpen ? <ChangePasswordDialog onClose={() => setPwOpen(false)} /> : null}
    </div>
  );
}

function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (next.length < 10) return setError(t('me.passwordTooShort'));
    try {
      await apiJson('/auth/change-password', { method: 'POST', json: { currentPassword: current, newPassword: next } });
      toast(t('common.saved'));
      onClose();
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={t('me.changePassword')} footer={<Button onClick={() => void submit()}>{t('common.save')}</Button>}>
      <div className="space-y-3">
        <Field label={t('me.currentPassword')}>
          <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label={t('me.newPassword')} hint={t('me.passwordHint')}>
          <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        {error ? <p className="text-red-700">{error}</p> : null}
      </div>
    </Dialog>
  );
}
